import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import AraSec from './AraSec';
import Ipucu from './Ipucu';

/**
 * "HANGİ KASAYA GİRDİ" — tahsilat işlenirken isteğe bağlı hesap seçimi.
 *
 * Tahsilat ekranı (faturasız alacak) ve sipariş ödeme parçası "tahsil edildi"
 * işaretlenirken kullanılır. Hesap seçilirse sunucu AYNI işlemde kasa girişi yazar;
 * seçilmezse eski davranış aynen sürer. Yalnız kasaya yazabilen role (`cash.write`)
 * görünür; tutarlar TL olduğu için yalnız TL hesapları listelenir.
 */
export type KasaHesabi = { id: number; ad: string; tur: 'KASA' | 'BANKA' | 'EMANET'; resmi: boolean; para: string; aktif: boolean; bakiyeKurus: number };

export function useKasaHesaplari() {
  const { can } = useAuth();
  const acik = can('cash.write');
  const [hesaplar, setHesaplar] = useState<KasaHesabi[] | null>(null);
  useEffect(() => {
    if (!acik) return;
    api<{ hesaplar: KasaHesabi[] }>('/api/kasa/hesap')
      .then((r) => setHesaplar(r.hesaplar.filter((h) => h.aktif && h.para === 'TRY')))
      .catch(() => setHesaplar([]));
  }, [acik]);
  return { acik, hesaplar };
}

const TUR = { KASA: 'Kasa', BANKA: 'Banka', EMANET: 'Emanet' } as const;

export default function KasaHesapSec({ id, hesaplar, deger, degis }: { id?: string; hesaplar: KasaHesabi[] | null; deger: string; degis: (id: string) => void }) {
  if (hesaplar && hesaplar.length === 0) {
    return <div className="ccd-meta">Kasa hareketi de yazılsın isterseniz önce <Link to="/kasa?sekme=tanimlar">Kasa ve Banka › Tanımlar</Link>'dan bir TL hesabı tanımlayın.</div>;
  }
  return (
    <AraSec id={id} yukleniyor={!hesaplar} deger={deger} degis={degis} bosEtiket="— kasaya yazma —" yerTutucu="Hangi kasaya girdi? (isteğe bağlı)"
            secenekler={(hesaplar ?? []).map((h) => ({ id: h.id, ad: h.ad, alt: `${TUR[h.tur]}${h.resmi ? '' : ' · gayriresmî'}` }))} />
  );
}

/** Çift tıklamaya karşı işlem anahtarı. */
export const kasaAnahtari = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID() : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

/**
 * Sipariş ödeme parçası için "tahsil edildi" çekmecesi: not + hangi kasaya girdi.
 * `onayla` hata fırlatırsa çekmece açık kalır ve ileti gösterilir.
 */
export function TahsilCekmece({ baslik, altBaslik, tutarKurus, hesaplar, kapat, onayla }: {
  baslik: string; altBaslik?: string; tutarKurus: number; hesaplar: KasaHesabi[] | null; kapat: () => void;
  onayla: (v: { not: string | null; hesapId: number | null; islemAnahtari: string }) => Promise<void>;
}) {
  const [not, setNot] = useState('');
  const [hesapId, setHesapId] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const anahtar = useRef(kasaAnahtari());

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBekliyor(true);
    try { await onayla({ not: not.trim() || null, hesapId: hesapId ? Number(hesapId) : null, islemAnahtari: anahtar.current }); kapat(); }
    catch (err) { setHata(err instanceof Error ? err.message : 'İşaretlenemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <div className="ccd-drawer-arka" onClick={() => !bekliyor && kapat()} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Tahsil edildi olarak işaretle">
        <form className="ccd-stack" onSubmit={gonder}>
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">Tahsilat</div><h2 className="ccd-heading">Tahsil edildi olarak işaretle</h2></div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={bekliyor}>Kapat</button>
          </div>
          <div className="ccd-kasa-icform">
            <b>{baslik}</b>
            {altBaslik && <div className="ccd-meta">{altBaslik}</div>}
            <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{formatTl(tutarKurus)}</div>
          </div>
          {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}
          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="tahsil-not">Not</label>
            <input id="tahsil-not" className="ccd-input" maxLength={300} autoFocus value={not} onChange={(e) => setNot(e.target.value)} placeholder="İsteğe bağlı: havale, çek, nakit…" />
          </div>
          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="tahsil-hesap">Hangi kasaya girdi <Ipucu id="kasa-bakiyesi" /></label>
            <KasaHesapSec id="tahsil-hesap" hesaplar={hesaplar} deger={hesapId} degis={setHesapId} />
            <div className="ccd-meta">İsteğe bağlı. Seçerseniz aynı anda o hesaba giriş hareketi yazılır; seçmezseniz yalnız "tahsil edildi" işareti konur.</div>
          </div>
          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'İşaretleniyor…' : 'Tahsil edildi'}</button>
        </form>
      </aside>
    </div>
  );
}
